// SPDX-License-Identifier: AGPL-3.0-or-later

import styles from '@app/features/channel/components/MessageComponentSelect.module.css';
import Channels from '@app/features/channel/state/Channels';
import Guilds from '@app/features/guild/state/Guilds';
import GuildMembers from '@app/features/member/state/GuildMembers';
import MemberSearch from '@app/features/member/state/MemberSearch';
import {clickMessageComponent} from '@app/features/messaging/commands/MessageComponentCommands';
import {ReactionImage} from '@app/features/messaging/components/ReactionImage';
import type {Message} from '@app/features/messaging/models/MessagingMessage';
import {useEmojiURL} from '@app/features/messaging/utils/ReactionUtils';
import Permission from '@app/features/permissions/state/Permission';
import * as PopoutCommands from '@app/features/ui/commands/PopoutCommands';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {
	SearchableListPopout,
	type SearchableListPopoutItem,
} from '@app/features/ui/popover/searchable_list_popout/SearchableListPopout';
import Users from '@app/features/user/state/Users';
import * as NicknameUtils from '@app/features/user/utils/NicknameUtils';
import {Permissions} from '@fluxer/constants/src/ChannelConstants';
import type {MessageActionRow} from '@fluxer/schema/src/domains/message/MessageComponentSchemas';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {CaretDownIcon, CheckIcon, HashIcon, UserIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {useEffect, useId, useMemo, useRef, useState} from 'react';

const SELECT_DESCRIPTOR = msg({message: 'Select…', comment: 'Placeholder of a bot select menu without its own text.'});
const SEARCH_DESCRIPTOR = msg({message: 'Search…', comment: 'Search field placeholder in a bot select menu.'});
const SEARCH_LABEL_DESCRIPTOR = msg({
	message: 'Search',
	comment: 'Accessible name of the bot select menu search field.',
});
const OPTIONS_DESCRIPTOR = msg({message: 'Options', comment: 'Accessible name of the bot select menu option list.'});
const NO_MATCHES_DESCRIPTOR = msg({message: 'No matches', comment: 'Bot select menu search found nothing.'});

type MessageSelectComponent = Exclude<MessageActionRow['components'][number], {type: 2}>;
type MessageComponentEmoji = NonNullable<NonNullable<MessageSelectComponent['options']>[number]['emoji']>;

const SelectTypes = {STRING: 3, USER: 5, ROLE: 6, MENTIONABLE: 7, CHANNEL: 8} as const;
const MEMBER_LIMIT = 25;
const MEMBER_SEARCH_DEBOUNCE_MS = 300;

interface SelectChoice {
	value: string;
	label: string;
	description?: string | null;
	emoji?: MessageComponentEmoji | null;
	kind: 'option' | 'user' | 'role' | 'channel';
	color?: string;
}

function OptionEmoji({emoji}: {emoji: MessageComponentEmoji}) {
	const url = useEmojiURL({emoji: {...emoji, animated: emoji.animated ?? false}});
	if (!url) return null;
	return (
		<ReactionImage
			src={url}
			alt={emoji.name}
			aria-hidden={true}
			draggable={false}
			className={styles.emoji}
			data-flx="channel.message-component-select.option-emoji.emoji"
		/>
	);
}

function ChoiceIcon({choice}: {choice: SelectChoice}) {
	if (choice.emoji)
		return <OptionEmoji emoji={choice.emoji} data-flx="channel.message-component-select.choice-icon.option-emoji" />;
	if (choice.kind === 'channel')
		return (
			<HashIcon
				className={styles.kindIcon}
				aria-hidden={true}
				data-flx="channel.message-component-select.choice-icon.hash-icon"
			/>
		);
	if (choice.kind === 'user')
		return (
			<UserIcon
				className={styles.kindIcon}
				aria-hidden={true}
				data-flx="channel.message-component-select.choice-icon.user-icon"
			/>
		);
	if (choice.kind === 'role')
		return (
			<span
				className={styles.roleDot}
				style={{backgroundColor: choice.color}}
				aria-hidden={true}
				data-flx="channel.message-component-select.choice-icon.role-dot"
			/>
		);
	return null;
}

function roleChoices(guildId: string | undefined): Array<SelectChoice> {
	const guild = guildId ? Guilds.getGuild(guildId) : undefined;
	if (!guild) return [];
	return Object.values(guild.roles)
		.filter((role) => role.id !== guild.id)
		.sort((a, b) => b.position - a.position)
		.map((role) => ({
			value: role.id,
			label: role.name,
			kind: 'role',
			color: role.color ? `#${role.color.toString(16).padStart(6, '0')}` : undefined,
		}));
}

function channelChoices(guildId: string | undefined, channelTypes?: ReadonlyArray<number>): Array<SelectChoice> {
	if (!guildId) return [];
	return Channels.getGuildChannels(guildId)
		.filter((channel) => !channelTypes?.length || channelTypes.includes(channel.type))
		.filter((channel) => Permission.can(Permissions.VIEW_CHANNEL, channel))
		.map((channel) => ({value: channel.id, label: channel.name ?? channel.id, kind: 'channel'}));
}

function userChoices(message: Message, query: string): Array<SelectChoice> {
	if (!message.guildId) {
		const recipients = Channels.getChannel(message.channelId)?.recipientIds ?? [];
		return recipients.flatMap((id) => {
			const user = Users.getUser(id);
			return user ? [{value: user.id, label: NicknameUtils.getNickname(user), kind: 'user' as const}] : [];
		});
	}
	const needle = query.trim().toLowerCase();
	const choices: Array<SelectChoice> = [];
	for (const member of GuildMembers.getMembers(message.guildId)) {
		const label = NicknameUtils.getNickname(member.user, message.guildId);
		if (needle && !label.toLowerCase().includes(needle) && !member.user.username.toLowerCase().includes(needle))
			continue;
		choices.push({value: member.user.id, label, kind: 'user'});
		if (choices.length === MEMBER_LIMIT) break;
	}
	return choices;
}

function choicesFor(select: MessageSelectComponent, message: Message, query: string): Array<SelectChoice> {
	switch (select.type) {
		case SelectTypes.STRING:
			return (select.options ?? []).map((option) => ({
				value: option.value,
				label: option.label,
				description: option.description,
				emoji: option.emoji,
				kind: 'option',
			}));
		case SelectTypes.USER:
			return userChoices(message, query);
		case SelectTypes.ROLE:
			return roleChoices(message.guildId);
		case SelectTypes.MENTIONABLE:
			return [...roleChoices(message.guildId), ...userChoices(message, query)];
		case SelectTypes.CHANNEL:
			return channelChoices(message.guildId, select.channel_types ?? undefined);
		default:
			return [];
	}
}

function initialValues(select: MessageSelectComponent): Array<string> {
	if (select.type === SelectTypes.STRING) {
		return (select.options ?? []).filter((option) => option.default).map((option) => option.value);
	}
	return (select.default_values ?? []).map((value) => value.id);
}

const SelectMenuPopout = observer(function SelectMenuPopout({
	select,
	message,
	selected,
	onToggle,
	onClose,
}: {
	select: MessageSelectComponent;
	message: Message;
	selected: ReadonlyArray<string>;
	onToggle: (value: string) => void;
	onClose: () => void;
}) {
	const {i18n} = useLingui();
	const [query, setQuery] = useState('');
	const searchesMembers = select.type === SelectTypes.USER || select.type === SelectTypes.MENTIONABLE;
	useEffect(() => {
		const guildId = message.guildId;
		const trimmed = query.trim();
		if (!searchesMembers || !guildId || !trimmed || GuildMembers.isGuildFullyLoaded(guildId)) return;
		const timer = setTimeout(() => {
			void MemberSearch.fetchMembersInBackground(trimmed, [guildId], guildId);
		}, MEMBER_SEARCH_DEBOUNCE_MS);
		return () => clearTimeout(timer);
	}, [query, searchesMembers, message.guildId]);
	const choices = choicesFor(select, message, query);
	const items = choices.map<SearchableListPopoutItem>((choice) => {
		const isSelected = selected.includes(choice.value);
		return {
			id: `${choice.kind}-${choice.value}`,
			ariaLabel: choice.label,
			searchValues: [choice.label, choice.description ?? '', choice.value],
			isSelected,
			onSelect: () => onToggle(choice.value),
			render: () => (
				<>
					<ChoiceIcon choice={choice} data-flx="channel.message-component-select.select-menu-popout.choice-icon" />
					<span
						className={styles.choiceText}
						data-flx="channel.message-component-select.select-menu-popout.choice-text"
					>
						<span
							className={styles.choiceLabel}
							data-flx="channel.message-component-select.select-menu-popout.choice-label"
						>
							{choice.label}
						</span>
						{choice.description ? (
							<span
								className={styles.choiceDescription}
								data-flx="channel.message-component-select.select-menu-popout.choice-description"
							>
								{choice.description}
							</span>
						) : null}
					</span>
					{isSelected ? (
						<CheckIcon
							weight="bold"
							className={styles.check}
							aria-hidden={true}
							data-flx="channel.message-component-select.select-menu-popout.check-icon"
						/>
					) : null}
				</>
			),
		};
	});
	return (
		<SearchableListPopout
			className={styles.menu}
			scrollerClassName={styles.menuScroller}
			optionClassName={styles.choice}
			placeholder={i18n._(SEARCH_DESCRIPTOR)}
			searchInputAriaLabel={i18n._(SEARCH_LABEL_DESCRIPTOR)}
			listAriaLabel={select.placeholder || i18n._(OPTIONS_DESCRIPTOR)}
			noResultsLabel={i18n._(NO_MATCHES_DESCRIPTOR)}
			sections={[{id: select.custom_id, items}]}
			onRequestClose={onClose}
			onSearchQueryChange={setQuery}
			disableInternalFiltering={searchesMembers}
			data-flx="channel.message-component-select.select-menu-popout.searchable-list-popout"
		/>
	);
});

export const MessageComponentSelect = observer(function MessageComponentSelect({
	message,
	select,
	isPreview,
}: {
	message: Message;
	select: MessageSelectComponent;
	isPreview: boolean;
}) {
	const {i18n} = useLingui();
	const popoutKey = useId();
	const triggerRef = useRef<HTMLButtonElement>(null);
	const [open, setOpen] = useState(false);
	const [pending, setPending] = useState(false);
	const [shown, setShown] = useState<ReadonlyArray<string>>(() => initialValues(select));
	const maxValues = Math.max(1, select.max_values ?? 1);
	const minValues = Math.min(select.min_values ?? 1, maxValues);
	const disabled = isPreview || Boolean(select.disabled) || pending;
	const labels = useMemo(() => {
		const known = new Map(choicesFor(select, message, '').map((choice) => [choice.value, choice] as const));
		return shown.map((value) => known.get(value)).filter((choice): choice is SelectChoice => choice != null);
	}, [select, message, shown]);

	const submit = async (values: ReadonlyArray<string>) => {
		setShown(values);
		setPending(true);
		try {
			await clickMessageComponent(i18n, message, select.custom_id, values);
		} finally {
			setPending(false);
			setShown(initialValues(select));
		}
	};

	const openMenu = () => {
		const target = triggerRef.current;
		if (!target || disabled) return;
		const draft = {values: [...shown], changed: false};
		const width = target.offsetWidth;
		const toggle = (value: string) => {
			if (maxValues === 1) {
				draft.values = [value];
				draft.changed = true;
				PopoutCommands.close(popoutKey);
				return;
			}
			const has = draft.values.includes(value);
			if (!has && draft.values.length >= maxValues) return;
			draft.values = has ? draft.values.filter((v) => v !== value) : [...draft.values, value];
			draft.changed = true;
			setShown(draft.values);
		};
		setOpen(true);
		PopoutCommands.open({
			key: popoutKey,
			target,
			position: 'bottom-start',
			returnFocusRef: triggerRef,
			containerClass: styles.menuLayer,
			onClose: () => {
				setOpen(false);
				if (draft.changed && draft.values.length >= minValues) void submit(draft.values);
				else setShown(initialValues(select));
			},
			render: ({onClose}) => (
				<div className={styles.menuSize} style={{width}} data-flx="channel.message-component-select.menu-size">
					<SelectMenuPopoutState
						select={select}
						message={message}
						initial={draft.values}
						onToggle={toggle}
						onClose={onClose}
						data-flx="channel.message-component-select.select-menu-popout-state"
					/>
				</div>
			),
		});
	};

	const text = labels.length > 0 ? labels.map((choice) => choice.label).join(', ') : null;
	return (
		<FocusRing offset={-2} data-flx="channel.message-component-select.focus-ring">
			<button
				ref={triggerRef}
				type="button"
				className={clsx(styles.trigger, open && styles.triggerOpen)}
				disabled={disabled}
				aria-haspopup="listbox"
				aria-expanded={open}
				aria-busy={pending || undefined}
				onClick={openMenu}
				data-flx="channel.message-component-select.trigger"
			>
				{labels.length === 1 ? (
					<ChoiceIcon choice={labels[0]} data-flx="channel.message-component-select.choice-icon" />
				) : null}
				<span
					className={clsx(styles.triggerText, !text && styles.placeholder)}
					data-flx="channel.message-component-select.trigger-text"
				>
					{text ?? (select.placeholder || i18n._(SELECT_DESCRIPTOR))}
				</span>
				<CaretDownIcon
					weight="bold"
					className={styles.caret}
					aria-hidden={true}
					data-flx="channel.message-component-select.caret-down-icon"
				/>
			</button>
		</FocusRing>
	);
});

function SelectMenuPopoutState({
	select,
	message,
	initial,
	onToggle,
	onClose,
}: {
	select: MessageSelectComponent;
	message: Message;
	initial: ReadonlyArray<string>;
	onToggle: (value: string) => void;
	onClose: () => void;
}) {
	const [selected, setSelected] = useState(initial);
	const maxValues = Math.max(1, select.max_values ?? 1);
	const toggle = (value: string) => {
		onToggle(value);
		setSelected((current) => {
			if (maxValues === 1) return [value];
			if (current.includes(value)) return current.filter((v) => v !== value);
			return current.length >= maxValues ? current : [...current, value];
		});
	};
	return (
		<SelectMenuPopout
			select={select}
			message={message}
			selected={selected}
			onToggle={toggle}
			onClose={onClose}
			data-flx="channel.message-component-select.select-menu-popout-state.select-menu-popout"
		/>
	);
}
